/**
 * Round-trip puro del formulario de historias editoriales.
 *
 * Vive separado de HistoriasForm.jsx por el mismo motivo que
 * descripcionJsonRoundtrip.js: poder testear el mapeo sin renderizar el
 * formulario completo (que arrastra Clerk, el router y el cliente HTTP).
 *
 * Spec: docs/superpowers/specs/2026-08-25-historias-editoriales-design.md
 */

/** Los 16 FancyColor del DS (flow ProductBand.tsx:6-22). Ni uno mas. */
export const COLORES_FANCY = [
  'cyan', 'blue', 'sky', 'violet', 'indigo', 'purple', 'pink', 'red',
  'orange', 'lime', 'teal', 'slate', 'dark-teal', 'dark-cyan',
  'dark-indigo', 'dark-yellow',
];

/**
 * Los tres tipos de bloque del cuerpo.
 *
 * `descripcion` no es adorno: el tipo se elige en tarjetas (`CampoRadioCards`)
 * y el nombre solo no dice como queda el bloque en la pagina publicada. Cada
 * texto describe lo que dibuja `VistaCuerpo` para ese tipo.
 */
export const TIPOS_BLOQUE = [
  { valor: 'parrafo', etiqueta: 'Párrafo', descripcion: 'Texto corrido del artículo.' },
  { valor: 'subtitulo', etiqueta: 'Subtítulo', descripcion: 'Encabezado que abre una sección.' },
  {
    valor: 'destacado',
    etiqueta: 'Cita destacada',
    descripcion: 'Frase en grande, con barra de color al costado.',
  },
];

export const CATEGORIAS = [
  { valor: 'B2C', etiqueta: 'B2C — persona' },
  { valor: 'B2B', etiqueta: 'B2B — empresa' },
];

/**
 * Los valores que ofrece el menu de tiempo de lectura.
 *
 * El string se guarda TAL CUAL y el sitio lo imprime sin tocarlo, en la
 * tarjeta del home y en el subtitulo del hero. Con texto libre convivian
 * "3 min de lectura", "3 minutos" y "3min" en la misma grilla. El menu fija
 * una redaccion; la columna sigue siendo texto.
 *
 * SON LOS TRES DEL TEMPLATE (`lecturaOpts` en EditorialHistorias.dc.html), no
 * un rango inventado: una version anterior de este archivo ofrecia de 1 a 10.
 * Un valor ya guardado fuera de la lista NO se pierde — `opcionesDeTiempo` lo
 * agrega como opcion "(valor actual)".
 */
export const OPCIONES_TIEMPO = [1, 2, 3].map((n) => ({
  value: `${n} min de lectura`,
  label: `${n} min de lectura`,
}));

/**
 * Las opciones, mas la vacia, mas el valor guardado si quedo fuera de la lista.
 *
 * Esa ultima parte es lo que impide una perdida de datos silenciosa: una
 * historia publicada con "3 minutos" no tendria su valor entre las opciones,
 * el select mostraria otra cosa, y guardar reescribiria el campo sin que nadie
 * lo pidiera.
 *
 * La opcion vacia va DENTRO de la lista y no como `placeholder`: el
 * placeholder de formkit deja esa opcion `disabled`, y aca "sin tiempo de
 * lectura" es un valor valido al que hay que poder volver.
 */
export function opcionesDeTiempo(actual) {
  const vacia = { value: '', label: 'Sin tiempo de lectura' };
  const v = String(actual || '').trim();
  const conocido = !v || OPCIONES_TIEMPO.some((o) => o.value === v);
  return conocido
    ? [vacia, ...OPCIONES_TIEMPO]
    : [vacia, { value: v, label: `${v} (valor actual)` }, ...OPCIONES_TIEMPO];
}

/* `imagen_posicion` esta aca por lo mismo que en la portada y en la banda: el
   editorial intermedio tambien lleva una foto, y sin encuadre la cara del
   protagonista queda cortada en el `EditorialSplit`. Es lo que el template del
   DS ya modelaba (CampoImagen con pos en la seccion "Editorial intermedio").
   Como `limpiarObjeto` descarta las claves en blanco, un medio sin encuadre se
   sigue guardando exactamente igual que antes. */
const CAMPOS_MEDIO = [
  'eyebrow', 'titulo', 'highlight', 'highlight_color', 'texto',
  'imagen_url', 'imagen_posicion',
];
const CAMPOS_BANDA = ['eyebrow', 'titulo', 'highlight', 'highlight_color', 'subtitulo', 'imagen_url', 'imagen_posicion'];
export const CAMPOS_CASO = ['antes', 'despues', 'problema', 'solucion', 'resultado'];

/**
 * Campos que son una lista y no una cadena.
 *
 * `highlight` guardaba UNA frase, y los bloques la buscaban entera dentro del
 * texto. Para resaltar dos palabras sueltas de una misma frase no habia forma:
 * escribir las dos juntas busca esa cadena literal, que no existe en el texto.
 * Ahora es una lista de terminos, como `palabrasDestacadas` en la configuracion
 * de planes.
 */
const CAMPOS_LISTA = ['highlight'];

const vacio = (campos) =>
  Object.fromEntries(campos.map((c) => [c, CAMPOS_LISTA.includes(c) ? [] : '']));

/**
 * Normaliza a la lista de terminos con la que trabaja el formulario.
 *
 * Acepta la cadena suelta del formato anterior: hay historias publicadas
 * guardadas asi, y una fila sin migrar no debe llegar al editor como vacia —
 * borraria el resaltado al guardar.
 */
export function terminosDesde(valor) {
  if (typeof valor === 'string') {
    const t = valor.trim();
    return t ? [{ texto: t, color: '' }] : [];
  }
  if (!Array.isArray(valor)) return [];
  return valor
    .filter((t) => t && typeof t.texto === 'string')
    .map((t) => ({
      texto: t.texto,
      color: typeof t.color === 'string' ? t.color : '',
    }));
}

/**
 * Lo que se guarda: sin filas vacias y sin la clave `color` cuando esta en
 * blanco, para que el termino tome el color del bloque.
 *
 * `null` y no `[]` cuando no queda nada: es lo que la columna entiende como
 * "sin resaltado".
 */
export function terminosHacia(valor) {
  const lista = terminosDesde(valor)
    .map((t) => ({ texto: t.texto.trim(), color: t.color.trim() }))
    .filter((t) => t.texto !== '')
    .map((t) => (t.color ? { texto: t.texto, color: t.color } : { texto: t.texto }));
  return lista.length ? lista : null;
}

export function formularioVacio() {
  return {
    slug: '',
    nombre: '',
    estado: 'borrador',
    categoria: 'B2C',
    destacada: false,
    orden: '',
    eyebrow: '',
    quote: '',
    highlight: [],
    highlight_color: '',
    dek: '',
    imagen_url: '',
    imagen_posicion: '',
    tiempo_lectura: '',
    fecha_publicacion: '',
    cuerpo: [{ tipo: 'parrafo', texto: '' }],
    stats: [],
    medio: vacio(CAMPOS_MEDIO),
    producto_banda: vacio(CAMPOS_BANDA),
    caso: vacio(CAMPOS_CASO),
    productos: [],
  };
}

/** Sin tildes, sin signos, separado por guiones. */
export function slugDesdeNombre(nombre) {
  return String(nombre || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Un campo sin contenido: cadena en blanco, o lista sin terminos utiles. */
function campoVacio(valor, campo) {
  if (CAMPOS_LISTA.includes(campo)) return terminosHacia(valor) === null;
  return !String(valor || '').trim();
}

export function medioVacio(medio) {
  return CAMPOS_MEDIO.every((c) => campoVacio(medio?.[c], c));
}

export function casoVacio(caso) {
  return CAMPOS_CASO.every((c) => !String(caso?.[c] || '').trim());
}

function bandaVacia(banda) {
  return CAMPOS_BANDA.every((c) => campoVacio(banda?.[c], c));
}

const SIMPLES = [
  'slug', 'nombre', 'estado', 'categoria', 'destacada', 'orden', 'eyebrow',
  'quote', 'highlight', 'highlight_color', 'dek', 'imagen_url',
  'imagen_posicion', 'tiempo_lectura', 'fecha_publicacion',
];

/** Normaliza dentro de un objeto anidado los campos que son lista. */
function conListas(obj) {
  const salida = { ...obj };
  for (const campo of CAMPOS_LISTA) {
    if (campo in salida) salida[campo] = terminosDesde(salida[campo]);
  }
  return salida;
}

export function desdeHistoria(historia) {
  const base = formularioVacio();
  return {
    ...base,
    ...Object.fromEntries(SIMPLES.map((k) => [k, historia[k] ?? base[k]])),
    cuerpo:
      Array.isArray(historia.cuerpo) && historia.cuerpo.length
        ? historia.cuerpo.map((b) => ({ tipo: b.tipo, texto: b.texto ?? '' }))
        : base.cuerpo,
    stats: Array.isArray(historia.stats)
      ? historia.stats.map((s) => ({ k: s.k ?? '', v: s.v ?? '' }))
      : [],
    highlight: terminosDesde(historia.highlight),
    medio: conListas({ ...vacio(CAMPOS_MEDIO), ...(historia.medio || {}) }),
    producto_banda: conListas({
      ...vacio(CAMPOS_BANDA),
      ...(historia.producto_banda || {}),
    }),
    caso: { ...vacio(CAMPOS_CASO), ...(historia.caso || {}) },
    productos: (historia.productos || []).map((p, i) => ({
      producto_id: p.producto_id,
      principal: Boolean(p.principal),
      orden: p.orden ?? i,
    })),
  };
}

const oNulo = (valor) => {
  const v = String(valor ?? '').trim();
  return v === '' ? null : v;
};

/** Solo las claves con contenido: no se guardan overrides en blanco. */
function limpiarObjeto(obj, campos) {
  return Object.fromEntries(
    campos
      .map((c) => [
        c,
        // Sin esta rama, `String([{texto: 'x'}])` guardaria "[object Object]".
        CAMPOS_LISTA.includes(c) ? terminosHacia(obj?.[c]) : String(obj?.[c] ?? '').trim(),
      ])
      .filter(([, v]) => v !== '' && v !== null)
  );
}

/** Convierte valor a número o null. Protege '' , null, undefined (explícitamente) antes de convertir. */
function ordenANumero(valor) {
  // Primero: descartar explícitamente '', null, undefined
  if (valor === '' || valor === null || valor === undefined) {
    return null;
  }
  // Después: convertir y validar con Number.isFinite
  const num = Number(valor);
  return Number.isFinite(num) ? num : null;
}

export function haciaEntrada(form) {
  return {
    slug: slugDesdeNombre(form.slug || form.nombre),
    nombre: String(form.nombre || '').trim(),
    estado: form.estado,
    categoria: form.categoria,
    destacada: Boolean(form.destacada),
    orden: ordenANumero(form.orden),
    eyebrow: oNulo(form.eyebrow),
    quote: String(form.quote || '').trim(),
    highlight: terminosHacia(form.highlight),
    highlight_color: oNulo(form.highlight_color),
    dek: oNulo(form.dek),
    imagen_url: String(form.imagen_url || '').trim(),
    imagen_posicion: oNulo(form.imagen_posicion),
    tiempo_lectura: oNulo(form.tiempo_lectura),
    fecha_publicacion: oNulo(form.fecha_publicacion),
    cuerpo: (form.cuerpo || [])
      .map((b) => ({ tipo: b.tipo, texto: String(b.texto || '').trim() }))
      .filter((b) => b.texto !== ''),
    stats: (form.stats || [])
      .map((s) => ({ k: String(s.k || '').trim(), v: String(s.v || '').trim() }))
      .filter((s) => s.k !== '' && s.v !== ''),
    medio: medioVacio(form.medio) ? null : limpiarObjeto(form.medio, CAMPOS_MEDIO),
    producto_banda: bandaVacia(form.producto_banda)
      ? null
      : limpiarObjeto(form.producto_banda, CAMPOS_BANDA),
    caso: casoVacio(form.caso) ? null : limpiarObjeto(form.caso, CAMPOS_CASO),
    productos: (form.productos || []).map((p, i) => ({
      producto_id: p.producto_id,
      principal: Boolean(p.principal),
      orden: p.orden ?? i,
    })),
  };
}
